<template>
  <v-menu
    v-model="menuOpen"
    offset-y
    :close-on-content-click="false"
    min-width="300"
    max-width="360"
    nudge-bottom="6"
    rounded="lg"
  >
    <template #activator="{ on, attrs }">
      <v-btn
        text
        class="text-none facility-switcher-btn px-2"
        v-bind="attrs"
        v-on="on"
      >
        <v-icon small color="primary" class="mr-2">
          mdi-office-building-marker
        </v-icon>
        <span class="switcher-label font-weight-medium text-truncate">
          {{ currentFacilityName }}
        </span>
        <v-icon small class="ml-1">
          {{ menuOpen ? "mdi-menu-up" : "mdi-menu-down" }}
        </v-icon>
      </v-btn>
    </template>

    <v-card>
      <div class="pa-2">
        <v-text-field
          v-model="search"
          autofocus
          dense
          outlined
          rounded
          hide-details
          clearable
          placeholder="Search Facilities…"
          prepend-inner-icon="mdi-magnify"
          clear-icon="mdi-close"
        />
      </div>
      <v-divider />

      <v-list dense class="facility-list py-0">
        <v-list-item v-if="loading">
          <v-list-item-content class="text-center py-3">
            <v-progress-circular indeterminate size="22" color="primary" />
          </v-list-item-content>
        </v-list-item>

        <v-list-item v-else-if="!displayedFacilities.length">
          <v-list-item-content>
            <v-list-item-title class="grey--text">
              {{ emptyMessage }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <template v-else>
          <v-list-item
            v-for="facility in displayedFacilities"
            :key="facility.id"
            :input-value="facility.id === currentFacilityId"
            color="primary"
            @click="choose(facility)"
          >
            <v-list-item-icon class="mr-2">
              <v-icon small>mdi-office-building-marker-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ facility.name }}</v-list-item-title>
              <v-list-item-subtitle v-if="locationCode(facility)">
                {{ locationCode(facility) }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-icon v-if="facility.id === currentFacilityId">
              <v-icon small color="primary">mdi-check</v-icon>
            </v-list-item-icon>
          </v-list-item>
        </template>
      </v-list>

      <template v-if="canReturnToOrg">
        <v-divider />
        <v-list dense class="py-0">
          <v-list-item @click="exitToRegion">
            <v-list-item-icon class="mr-2">
              <v-icon small>mdi-arrow-left</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>
                Back to {{ returnTarget.name }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </template>
    </v-card>
  </v-menu>
</template>

<script>
import useUser from "../authentication/useUser";
import useFacility from "../authentication/useFacility";
import useAccessibleScope from "../authentication/useAccessibleScope";
import { useRouter } from "vue-router/composables";
import { debounce } from "@tod-ui/helpers/utils";
import {
  facilityLocationCode,
  matchesFacility,
} from "../common/helpers/facility";
import { LIST_ORGANIZATION_DESCENDANTS } from "../../graphql/organizations";

export default {
  name: "FacilitySwitcher",
  setup() {
    const { defaultOrganization, defaultPage } = useUser();
    const {
      reachesMultipleFacilities,
      accessibleFacilities,
      scopeRootOrganization,
      hasSystemScope,
      topLevelOrganization,
    } = useAccessibleScope();
    const {
      currentFacility,
      currentFacilityId,
      selectFacility,
      clearCurrentFacility,
    } = useFacility();
    const router = useRouter();

    return {
      defaultOrganization,
      defaultPage,
      reachesMultipleFacilities,
      scopeRootOrganization,
      hasSystemScope,
      topLevelOrganization,
      accessibleFacilities,
      currentFacility,
      currentFacilityId,
      selectFacility,
      clearCurrentFacility,
      router,
    };
  },
  data() {
    return {
      menuOpen: false,
      search: "",
      loading: false,
      serverResults: [],
      minSearchLength: 3,
    };
  },
  computed: {
    currentFacilityName() {
      return this.currentFacility?.name || "Select Facility";
    },
    // Where leaving the facility lands: the top of the user's own scope tree.
    // A manager whose scope spans regions or organizations has a default org
    // that is often one facility inside that scope, so anchoring on the scope
    // root is what gives them somewhere above the current facility to return to.
    returnTarget() {
      // A system admin's scope root is the platform/application org their role
      // grants, not the bounded facility node, so anchor the return there.
      if (this.hasSystemScope && this.topLevelOrganization) {
        return this.topLevelOrganization;
      }

      const root = this.scopeRootOrganization;
      if (root && root.type !== "facility") return root;

      const fallback = this.defaultOrganization;
      return fallback && fallback.type !== "facility" ? fallback : null;
    },
    canReturnToOrg() {
      return Boolean(this.currentFacilityId && this.returnTarget);
    },
    // `defaultPage` resolves to a facility dashboard when the user's default org
    // is a facility, which is the context this button exists to leave. The
    // org-wide summary is the destination in that case.
    exitRoute() {
      const target = this.defaultPage;
      return target?.name === "FacilityDashboard"
        ? { name: "ManagementSummary" }
        : target;
    },
    isServerSearchMode() {
      // Platform/application admins have no bounded client scope (their sub-tree
      // is the whole system, deliberately kept out of the login payload), so the
      // switcher searches the server as the user types rather than filtering a
      // client list. `hasSystemScope` covers an admin who also holds lower
      // region/facility roles and whose default org is therefore a facility.
      return (
        this.hasSystemScope ||
        ["platform", "application"].includes(this.defaultOrganization?.type)
      );
    },
    // The organization the server search is rooted at: the top-level org a
    // system admin holds a role on, so the search spans everything under it and
    // not just their default facility's own sub-tree.
    serverSearchRootId() {
      return this.topLevelOrganization?.id || this.defaultOrganization?.id;
    },
    displayedFacilities() {
      const byName = (a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, {
          sensitivity: "base",
        });

      // Super-admin: show the server-search results (populated as the user types,
      // matched against the backend's tsvector — name or Location Code, SS-105).
      if (this.isServerSearchMode) {
        return [...this.serverResults].sort(byName);
      }

      // Bounded manager: filter the accessible-facility union client-side by name
      // or SiteLink Location Code. Copy before sorting — `accessibleFacilities` is
      // a readonly scope ref — so the list reads A–Z (case-insensitive).
      const query = this.search?.trim();
      const facilities = query
        ? this.accessibleFacilities.filter((facility) =>
            matchesFacility(facility, query)
          )
        : [...this.accessibleFacilities];
      return facilities.sort(byName);
    },
    emptyMessage() {
      if (
        this.isServerSearchMode &&
        (this.search?.trim().length || 0) < this.minSearchLength
      ) {
        return "Type to search facilities";
      }
      return "No facilities found";
    },
  },
  watch: {
    search(value) {
      const text = value?.trim() || "";
      if (text.length < this.minSearchLength) {
        this.serverResults = [];
        return;
      }
      this.debouncedSearch(text);
    },
    menuOpen(open) {
      // Reset the query each time it opens so the list starts clean; seed the
      // super-admin list with the current facility so it's visible before typing.
      if (open) {
        this.search = "";
        this.serverResults = this.currentFacility ? [this.currentFacility] : [];
      }
    },
  },
  created() {
    this.debouncedSearch = debounce(this.runServerSearch);
  },
  beforeDestroy() {
    this.debouncedSearch?.cancel?.();
  },
  methods: {
    locationCode(facility) {
      return facilityLocationCode(facility);
    },
    async runServerSearch(searchText) {
      this.loading = true;
      try {
        const {
          data: { organizationDescendants },
        } = await this.$apollo.query({
          query: LIST_ORGANIZATION_DESCENDANTS,
          variables: {
            id: this.serverSearchRootId,
            searchText,
            type: "facility",
          },
        });
        this.serverResults = (organizationDescendants || []).filter(
          (org) => !org.isDemo
        );
      } catch (error) {
        console.error(error);
        this.serverResults = [];
      } finally {
        this.loading = false;
      }
    },
    choose(facility) {
      this.menuOpen = false;
      if (facility.id === this.currentFacilityId) return;
      // Establish the facility context (sets currentFacility synchronously, then
      // hydrates via GET_FACILITY) and route to its dashboard ourselves. We drive
      // the navigation directly rather than via the global redirectToDashboard
      // flag, which isn't reset and so only fires once per session.
      this.selectFacility(this.$apollo, facility);
      this.router
        .replace({
          name: "FacilityDashboard",
          params: { organization_id: facility.id },
        })
        .catch((err) => {
          if (err.name !== "NavigationDuplicated") throw err;
        });
    },
    exitToRegion() {
      // Clear the selected facility and return to the regional/org-level home.
      this.clearCurrentFacility();
      this.menuOpen = false;
      return this.router.replace(this.exitRoute).catch((err) => {
        if (err.name !== "NavigationDuplicated") throw err;
      });
    },
  },
};
</script>

<style scoped>
.switcher-label {
  max-width: 200px;
  display: inline-block;
  vertical-align: middle;
}
.facility-list {
  max-height: 320px;
  overflow-y: auto;
}
</style>
